import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api, type Me } from '../lib/api';
import { Button, cx } from './ui';

const NAV = [
  { to: '/sites', label: 'Websites', icon: GlobeIcon },
  { to: '/organizations', label: 'Mandanten', icon: BuildingIcon },
];

/**
 * Rahmen der Anwendung.
 *
 * Mobile First: unter 1024px eine Kopfzeile mit Schublade, darueber eine feste
 * Seitenleiste. Das entspricht der adaptiven Navigation aus dem Design-System,
 * und die Navigation bleibt an derselben Stelle, egal auf welcher Seite.
 */
export function AppShell({ me, children }: { me: Me; children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  async function logout() {
    await api.logout().catch(() => undefined);
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[264px_1fr]">
      {/* Kopfzeile nur auf kleinen Bildschirmen */}
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-[var(--border)] bg-[var(--surface)]/95 px-4 py-3 backdrop-blur lg:hidden">
        <Logo />
        <button
          type="button"
          onClick={() => setDrawerOpen((open) => !open)}
          aria-expanded={drawerOpen}
          aria-controls="main-nav"
          className="inline-flex size-11 cursor-pointer items-center justify-center rounded-xl border border-[var(--border)]"
        >
          <span className="sr-only">Navigation</span>
          <MenuIcon open={drawerOpen} />
        </button>
      </header>

      {drawerOpen && (
        <button
          type="button"
          aria-label="Navigation schliessen"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-30 bg-ink-900/50 lg:hidden"
        />
      )}

      <nav
        id="main-nav"
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex w-[min(280px,82vw)] flex-col border-r border-[var(--border)] bg-[var(--surface)] px-4 py-5 transition-transform duration-250 ease-[var(--ease-out-soft)] lg:static lg:z-auto lg:w-auto lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="mb-6 hidden lg:block">
          <Logo />
        </div>

        <ul className="flex flex-1 flex-col gap-1">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  cx(
                    'flex min-h-11 items-center gap-3 rounded-xl px-3 text-[15px] font-medium transition-colors',
                    isActive
                      ? 'bg-brand-600 text-white'
                      : 'text-[var(--text-muted)] hover:bg-brand-50/70 hover:text-[var(--text)]',
                  )
                }
              >
                <item.icon />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="mt-6 border-t border-[var(--border)] pt-4">
          <NavLink
            to="/konto"
            className={({ isActive }) =>
              cx(
                'block rounded-xl px-3 py-2 transition-colors',
                isActive ? 'bg-brand-50/70' : 'hover:bg-brand-50/50',
              )
            }
          >
            <span className="block truncate text-sm font-medium">{me.user.name ?? me.user.email}</span>
            <span className="block truncate text-xs text-[var(--text-muted)]">
              {me.user.platformRole === 'platform_admin' ? 'Plattform-Admin' : 'Konto'} · Einstellungen
            </span>
          </NavLink>
          <Button variant="ghost" size="sm" className="mt-2 w-full justify-start" onClick={logout}>
            Abmelden
          </Button>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:py-10">{children}</main>
    </div>
  );
}

function Logo() {
  return (
    <span className="flex items-center gap-2.5">
      {/* Feste Größe, damit beim Laden nichts springt (CLS). */}
      <img
        src="/brand/crumblemonster-96.webp"
        alt=""
        width={36}
        height={36}
        className="size-9 shrink-0"
        loading="eager"
        decoding="async"
      />
      <span className="text-[17px] font-semibold tracking-tight">ilovecookies</span>
    </span>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path d="M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M14 10h5a1 1 0 0 1 1 1v10M4 21h16" strokeLinecap="round" />
      <path d="M8 8h2M8 12h2M8 16h2M17 14h1M17 17h1" strokeLinecap="round" />
    </svg>
  );
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
    </svg>
  );
}
